import {
  animate,
  group,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { cloudOfflineOutline, refreshOutline } from 'ionicons/icons';
import { TranslatePipe } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

import { BtnComponent } from '../btn/btn.component';
import { AppResumeService } from 'src/app/providers/app-resume.service';
import {
  ConnectivityService,
  ConnectivityState,
} from 'src/app/providers/connectivity.service';

@Component({
  selector: 'app-connectivity-overlay',
  standalone: true,
  templateUrl: './connectivity-overlay.component.html',
  styleUrls: ['./connectivity-overlay.component.scss'],
  imports: [NgIf, AsyncPipe, IonIcon, IonSpinner, TranslatePipe, BtnComponent],
  animations: [
    trigger('connectivityOverlay', [
      transition(':enter', [
        group([
          query(
            '.connectivity-backdrop',
            [
              style({ opacity: 0 }),
              animate('280ms ease', style({ opacity: 1 })),
            ],
            { optional: true }
          ),
          query(
            '.connectivity-container',
            [
              style({ opacity: 0, transform: 'scale(1.08)' }),
              animate(
                '320ms cubic-bezier(0.22, 1, 0.36, 1)',
                style({ opacity: 1, transform: 'scale(1)' })
              ),
            ],
            { optional: true }
          ),
        ]),
      ]),
      transition(':leave', [
        group([
          query(
            '.connectivity-container',
            [
              animate(
                '260ms cubic-bezier(0.4, 0, 0.2, 1)',
                style({ opacity: 0, transform: 'scale(1.08)' })
              ),
            ],
            { optional: true }
          ),
          query(
            '.connectivity-backdrop',
            [
              animate('260ms ease', style({ opacity: 0 })),
            ],
            { optional: true }
          ),
        ]),
      ]),
    ]),
  ],
})
export class ConnectivityOverlayComponent implements OnInit {
  /** When true, stack below splash (z-index 20000); otherwise block the full app. */
  @Input() underSplash = false;

  /** Local guard so Retry shows checking UI until the async check finishes. */
  retryInFlight = false;

  /** Brief shake when a manual retry still finds no connection. */
  showRetryShake = false;

  readonly vm$: Observable<{
    visible: boolean;
    checking: boolean;
    offline: boolean;
  }> = this.connectivityService.state$.pipe(
    map((state: ConnectivityState) => ({
      visible: state.status === 'offline' || state.status === 'checking',
      checking: state.status === 'checking',
      offline: state.status === 'offline',
    }))
  );

  constructor(
    private connectivityService: ConnectivityService,
    private appResumeService: AppResumeService
  ) {}

  ngOnInit(): void {
    addIcons({ cloudOfflineOutline, refreshOutline });
  }

  onRetry(): void {
    if (this.retryInFlight) {
      return;
    }

    this.retryInFlight = true;
    this.showRetryShake = false;

    void this.appResumeService.run().finally(() => {
      this.retryInFlight = false;

      if (this.connectivityService.isOffline) {
        this.showRetryShake = true;
        setTimeout(() => {
          this.showRetryShake = false;
        }, 500);
      }
    });
  }
}
